import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  Home, Compass, Plane, MessageCircle, User, LayoutList, ShoppingBag,
  Bell, Wallet, LogOut, Shield, Globe2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { Logo } from "./Logo";
import { LangToggle } from "./common";
import api from "../lib/api";

function navFor(role, t) {
  const common = [
    { to: "/home", label: t("nav_home"), icon: Home },
    { to: "/explore", label: t("nav_explore"), icon: Compass },
  ];
  if (role === "bawainer") {
    return [
      ...common,
      { to: "/post-trip", label: t("nav_posttrip"), icon: Plane, primary: true },
      { to: "/messages", label: t("nav_messages"), icon: MessageCircle },
      { to: "/profile", label: t("nav_profile"), icon: User },
    ];
  }
  if (role === "admin") {
    return [
      { to: "/admin", label: t("nav_admin"), icon: Shield },
      { to: "/explore", label: t("nav_explore"), icon: Compass },
      { to: "/home", label: t("nav_home"), icon: Home },
      { to: "/messages", label: t("nav_messages"), icon: MessageCircle },
      { to: "/profile", label: t("nav_profile"), icon: User },
    ];
  }
  return [
    ...common,
    { to: "/post-request", label: t("nav_board"), icon: LayoutList, primary: true },
    { to: "/messages", label: t("nav_messages"), icon: MessageCircle },
    { to: "/profile", label: t("nav_profile"), icon: User },
  ];
}

function desktopLinks(role, t) {
  if (role === "bawainer")
    return [
      { to: "/home", label: t("nav_home") }, { to: "/explore", label: t("nav_explore") },
      { to: "/whos-going", label: t("nav_whosgoing") }, { to: "/my-trips", label: t("nav_trips") },
      { to: "/requests", label: t("nav_requests") }, { to: "/wallet", label: t("nav_wallet") },
      { to: "/messages", label: t("nav_messages") }, { to: "/orders", label: t("nav_orders") },
    ];
  if (role === "admin")
    return [{ to: "/admin", label: t("nav_admin") }, { to: "/explore", label: t("nav_explore") }];
  return [
    { to: "/home", label: t("nav_home") }, { to: "/explore", label: t("nav_explore") },
    { to: "/whos-going", label: t("nav_whosgoing") }, { to: "/post-request", label: t("nav_board") },
    { to: "/requests", label: t("nav_requests") }, { to: "/orders", label: t("nav_orders") },
    { to: "/messages", label: t("nav_messages") },
  ];
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (active) setUnread(data.unread);
      } catch {}
    };
    load();
    const id = setInterval(load, 15000);
    return () => { active = false; clearInterval(id); };
  }, [location.pathname]);

  const role = user?.role;
  const bottom = navFor(role, t);
  const top = desktopLinks(role, t);
  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <div className="min-h-screen bg-cloud">
      {/* Top bar */}
      <header className="sticky top-0 z-40 glass border-b border-black/5">
        <div className="max-w-screen-xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <Link to="/home" data-testid="topnav-logo"><Logo size={30} /></Link>
          <nav className="hidden lg:flex items-center gap-1">
            {top.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`topnav-${l.to.slice(1)}`}
                className={`px-3 py-2 rounded-full text-sm font-semibold transition-colors ${
                  isActive(l.to) ? "bg-navy text-white" : "text-navy hover:bg-white"}`}>
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <LangToggle />
            <button onClick={() => navigate("/notifications")} data-testid="notif-bell"
              className="relative w-9 h-9 rounded-full bg-white flex items-center justify-center hover:bg-cloud border border-black/5">
              <Bell size={17} className="text-navy" />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-lime text-navy text-[10px] font-bold flex items-center justify-center">
                  {unread}
                </span>
              )}
            </button>
            <button onClick={logout} data-testid="logout-btn"
              className="hidden sm:flex w-9 h-9 rounded-full bg-white items-center justify-center hover:bg-cloud border border-black/5">
              <LogOut size={16} className="text-navy" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-4 pt-4 pb-28 lg:pb-12 animate-fade-up">
        <Outlet />
      </main>

      {/* Bottom nav (mobile) */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 glass border-t border-black/5"
        data-testid="bottom-nav">
        <div className="grid grid-cols-5 h-16">
          {bottom.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            if (item.primary) {
              return (
                <Link key={item.to} to={item.to} data-testid={`bottomnav-${item.to.slice(1)}`}
                  className="flex items-center justify-center">
                  <span className="-mt-6 w-14 h-14 rounded-full bg-lime shadow-lift flex items-center justify-center border-4 border-cloud">
                    <Icon size={24} className="text-navy" strokeWidth={2.5} />
                  </span>
                </Link>
              );
            }
            return (
              <Link key={item.to} to={item.to} data-testid={`bottomnav-${item.to.slice(1)}`}
                className={`flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold ${
                  active ? "text-navy" : "text-muted-foreground"}`}>
                <Icon size={20} strokeWidth={active ? 2.5 : 2} />
                {item.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
